// src/app/videos/SignupGateCard.tsx
//
// The soft gate on a video page, shown to anonymous visitors in place of the remaining
// worked problems and the Learn / Practise exits. Same shape as the sign-up card on
// /try/results: one primary "Create free account" that returns the visitor to THIS page
// after signup, and a quiet sign-in line for people who already have an account.
//
// Server-safe on purpose (no hooks) so the video page can stay a server component.

import Link from 'next/link'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export default function SignupGateCard({
  returnTo,
  hiddenCount,
  topic,
}: {
  /** Path to land back on after signup — the video the visitor was reading. */
  returnTo: string
  /** How many worked problems are behind the gate (0 hides that line). */
  hiddenCount: number
  topic: string
}) {
  const cb = encodeURIComponent(returnTo)
  return (
    <Card className="border-brand/50 bg-card/60 text-foreground" data-testid="signup-gate">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-muted-foreground">
          <Lock className="h-4 w-4 text-brand-ink" aria-hidden="true" />
          Keep going — free
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-muted-foreground">
        <p>
          Create your free account to see{' '}
          {hiddenCount > 0 ? (
            <>
              the{' '}
              {hiddenCount === 1 ? 'other worked problem' : `other ${hiddenCount} worked problems`}
              ,{' '}
            </>
          ) : null}
          the written lesson on <span className="font-medium text-foreground">{topic}</span>, and
          practise real SAT questions on it.
        </p>
        <Button asChild className="w-full bg-brand text-on-brand">
          <Link href={`/signup?callbackUrl=${cb}`}>Create free account</Link>
        </Button>
        <p className="pt-1 text-center text-xs">
          Already have an account?{' '}
          <Link href={`/signin?callbackUrl=${cb}`} className="underline decoration-slate-500">
            Sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
